/**
 * Tool definitions for the Tool Use demo (Story 305).
 *
 * Three pre-baked tools per spec. Each is a plain function so the view can
 * dispatch them deterministically; nothing here speaks to the network or to
 * the Anthropic SDK directly. The view glues them to the library's
 * `runToolUseLoop` (Story 201).
 */
import { evaluate } from './calc';

export interface DemoTool {
  name: string;
  description: string;
  run(input: unknown): Promise<unknown>;
}

export interface DocResult {
  title: string;
  snippet: string;
  url: string;
}

const MOCK_DOCS: DocResult[] = [
  {
    title: 'ClaudeProvider — connect',
    snippet: 'Establishes a connection and returns the underlying Anthropic client.',
    url: 'https://example.com/wiki/ClaudeProvider#connect',
  },
  {
    title: 'Conversation — send',
    snippet: 'Multi-turn helper that appends user/assistant pairs atomically.',
    url: 'https://example.com/wiki/Conversation#send',
  },
  {
    title: 'ResponseCache — TTL semantics',
    snippet: 'LRU cache keyed on canonicalised request shape; default TTL 60s.',
    url: 'https://example.com/wiki/ResponseCache',
  },
];

export const getCurrentTimeTool: DemoTool = {
  name: 'getCurrentTime',
  description: 'Returns the current ISO 8601 timestamp.',
  async run() {
    return new Date().toISOString();
  },
};

export const searchDocsTool: DemoTool = {
  name: 'searchDocs',
  description: 'Search the (mock) documentation corpus for matching pages.',
  async run(input) {
    const query = typeof input === 'object' && input !== null && 'query' in input
      ? String((input as { query: unknown }).query)
      : '';
    const needle = query.toLowerCase();
    const results = MOCK_DOCS.filter((d) =>
      d.title.toLowerCase().includes(needle) || d.snippet.toLowerCase().includes(needle),
    );
    return { results };
  },
};

export const calculateTool: DemoTool = {
  name: 'calculate',
  description: 'Evaluate a math expression using +, -, *, /, parentheses.',
  async run(input) {
    const expression = typeof input === 'object' && input !== null && 'expression' in input
      ? String((input as { expression: unknown }).expression)
      : '';
    return evaluate(expression);
  },
};

export const ALL_TOOLS: ReadonlyArray<DemoTool> = [
  getCurrentTimeTool,
  searchDocsTool,
  calculateTool,
];
